import { HttpClient } from '@angular/common/http';
import { EventEmitter, Inject } from '@angular/core';

import { closest } from 'fastest-levenshtein';

import { GlobalConstants, StageConstants } from 'core/constants';
import { CriticalError } from 'core/error/error';
import { ErrorService } from 'core/error/error.service';
import { Globals } from 'core/globals';
import { Severity } from 'core/logging/constants';
import { LoggingService } from 'core/logging/logging.service';
import { ProductService } from 'core/product/product.service';
import { Utils } from 'core/utils';
import { StageUtils } from 'core/utils.stage';
import { AsyncEventEmitter } from 'core/utils/async-event-emitter';
import { PartConfig, StepConfig, TemplateConfig, TemplateGroupConfig,
         TemplatesSettings
       } from '../collage-designer-config';
import { PlaceholderType } from '../stage/placeholder/placeholder';
import { DesignState } from '../stage/stage-config';
import { ConditionMatcher } from './condition-matcher';

export interface PartLocation {
  designStepId: number;
  part: PartConfig;
}

interface TemplateSelection {
  templateGroupId: string | undefined;
  templateId: string | undefined;
}

export interface AvailableTemplate {
  isDefaultGroup: boolean;
  isDefaultTemplate: boolean;
  tag: string | undefined;
  templateGroupId: string;
  templateId: string;
}

class CriticalTemplateSelectionError extends CriticalError
{
  protected _name: string = 'CriticalTemplateSelectionError';
}

export class DesignStepService
{
  public designStepChanged: EventEmitter<StepConfig> = new EventEmitter();
  public templateChanged: AsyncEventEmitter<TemplateConfig> =
      new AsyncEventEmitter();
  public partChanged: EventEmitter<PartConfig> = new EventEmitter();
  public createNewPlaceholderInitialized: EventEmitter<PlaceholderType> =
      new EventEmitter();

  private currentDesignStep: DesignState;
  private currentProductId: string;
  private designSteps: Array<DesignState> = [];
  private stepConfigs: Array<StepConfig> = [];
  private previousStepConfig: Array<StepConfig> = [];
  private conditionMatcher: ConditionMatcher;
  private resolvedTemplates: {[url: string]: string} = {};

  constructor(
      @Inject(HttpClient) private http: HttpClient,
      @Inject(ProductService) private productService: ProductService,
      @Inject(ErrorService) private errorService,
      @Inject(LoggingService) private loggingService) {}

  public setStepConfigs(productId: string,
                        catalogStoreId: string,
                        stepConfigs: Array<StepConfig>):
      void
  {
    this.previousStepConfig = this.stepConfigs;
    this.currentProductId = productId;
    this.stepConfigs = stepConfigs;
    this.conditionMatcher = new ConditionMatcher(catalogStoreId, this);
  }

  public getDesignStepConfig(designState?: DesignState): StepConfig
  {
    const state = designState || this.currentDesignStep;
    return this.stepConfigs[state.designStepId];
  }

  public async setDesignStep(id: number): Promise<void>
  {
    const templateSelection = this.getTemplateSelection();

    const possibleDesignSteps = this.designSteps.filter(
        d => d.designStepId === id);
    if (possibleDesignSteps.length === 0) {
      this.currentDesignStep = <DesignState> {
        designStepId: id,
      };
      this.designSteps.push(this.currentDesignStep);
    }
    else {
      this.currentDesignStep = possibleDesignSteps[0];
    }

    await this.conditionMatcher.determinePartOrientationMapping();
    await this.setPartId(this.getDesignablePart().id, templateSelection);
    this.designStepChanged.emit(this.stepConfigs[id]);
  }

  public getDesignablePart(): PartConfig
  {
    const designableParts = (
      this.getParts().filter(
        p => StageConstants.DESIGNABLE_PART_IDS.indexOf(p.editingView.type) !==
          -1));

    return designableParts[0];
  }

  public getDesignStepId(): number
  {
    if (this.currentDesignStep !== undefined) {
      return this.currentDesignStep.designStepId;
    }
    else {
      return 0;
    }
  }

  public getProductId(): string
  {
    return this.currentProductId;
  }

  private findTemplateByPreviousProduct(
      partId: string,
      availableTemplates: Array<AvailableTemplate>
      ): undefined | AvailableTemplate
  {
    const previousParts = Utils.flattenArray(this.previousStepConfig.map(
        s => s.parts.filter(p => p.id === partId)));

    if (previousParts.length === 0) {
      return undefined;
    }

    const previousPartTemplateGroups = previousParts[0].templateGroups.filter(
        g => g.isDefault);
    if (previousPartTemplateGroups.length === 0) {
      return undefined;
    }

    const previousTemplates = Utils
      .objectValuesToArray(previousPartTemplateGroups[0].templates)
      .filter(t => t.isDefault);
    if (previousTemplates.length === 0) {
      return undefined;
    }

    const previousTemplate = previousTemplates[0];
    if (previousTemplate.tag) {
      availableTemplates = availableTemplates.filter(
          t => t.tag === previousTemplate.tag);
    }
    if (availableTemplates.length === 0) {
      return undefined;
    }

    const closestTemplate = closest(
        previousTemplate.id, availableTemplates.map(t => t.templateId));
    return availableTemplates.filter(t => t.templateId === closestTemplate)[0];
  }

  private findTemplateByPreviousTag(
      availableTemplates: Array<AvailableTemplate>
      ): undefined | AvailableTemplate
  {
    const currentDesignStepId = this.currentDesignStep.designStepId;

    const previousDesignStep = Utils.find(
        this.designSteps,
        (ds: DesignState) => ds.designStepId === currentDesignStepId - 1);

    if (! previousDesignStep) {
      return undefined;
    }

    const previousTemplate = this.getTemplate(previousDesignStep);

    if (! previousTemplate) {
      return undefined;
    }

    const previousTag = previousTemplate.tag;

    if (! previousTag) {
      return undefined;
    }

    return Utils.find(availableTemplates, (t) => t.tag === previousTag);
  }

  private async setPartId(
      partId: string, templateSelection: TemplateSelection): Promise<void>
  {
    this.currentDesignStep.partId = partId;

    const templateGroups = this.getTemplateGroups();

    const availableTemplates = Utils.flattenArray(
      Utils.byValue(templateGroups)
        .map(g => Utils.byValue(g.templates).map(
          t => (<AvailableTemplate>{
            templateGroupId: g.id,
            templateId: t.id,
            isDefaultGroup: g.isDefault,
            isDefaultTemplate: t.isDefault,
            tag: t.tag,
          }))));

    const firstTemplate = availableTemplates[0];

    const defaultTemplate = Utils.find(
      availableTemplates, t => t.isDefaultTemplate && t.isDefaultGroup);

    const defaultGroup = Utils.find(availableTemplates, t => t.isDefaultGroup);

    const lastUsedTemplate = Utils.find(
      availableTemplates, t =>
        t.templateGroupId === templateSelection.templateGroupId &&
        t.templateId === templateSelection.templateId);

    const previousProductTemplate = this.findTemplateByPreviousProduct(
        partId, availableTemplates);
    const previousStepTagTemplate = this.findTemplateByPreviousTag(
        availableTemplates);

    const selectedTemplate =
        previousProductTemplate ||
        previousStepTagTemplate ||
        lastUsedTemplate ||
        defaultTemplate ||
        defaultGroup ||
        firstTemplate;

    if (!selectedTemplate) {
      const error = new CriticalTemplateSelectionError(
          'no matching template found', [partId]);
      this.errorService.add(error, 'DesignStepService');
      this.errorService.flush('DesignStepService');
    }

    await this.setTemplateGroupId(
      selectedTemplate.templateGroupId,
      selectedTemplate.templateId);

    this.partChanged.emit(this.getPart());
  }

  public getPartId(): string
  {
    return this.currentDesignStep.partId;
  }

  public getParts(designState?: DesignState): Array<PartConfig>
  {
    const state = designState || this.currentDesignStep;

    if (state !== undefined) {
      return this.getDesignStepConfig(state).parts;
    }
    else {
      return [];
    }
  }

  public getPart(designState?: DesignState): PartConfig
  {
    const state = designState || this.currentDesignStep;
    return this.getParts(state).filter(p => p.id === state.partId)[0];
  }

  public getToolboxesNames(): Array<string>
  {
    let toolboxes: Array<string> = [];

    // Die ProductChooserToolbox wird an sich immer benötigt. Ob die Toolbox
    // wirklich angzeigt wird, wird anhand der Produkte entschieden
    toolboxes.push('ProductChooserToolbox');

    const part = this.getPart();
    if (part.previewUrl) {
      toolboxes.push('PreviewToolbox');
    }

    if (this.getTemplateGroups().length > 1 ||
        Object.keys(this.getTemplates()).length > 1) {
      toolboxes.push('LayoutToolbox');
    }

    if (this.changeableImagePlaceholdersAvailable()) {
      toolboxes.push('GalleryToolbox');
    }

    return toolboxes;
  }

  public changeableImagePlaceholdersAvailable(): boolean
  {
    if (Globals.mode === GlobalConstants.MODE_GOD) {
      return true;
    }

    const placeholders = this.getTemplate().imagePlaceholders;
    return (placeholders.portrait + placeholders.landscape) > 0;
  }

  public async setTemplateGroupId(
      id: string, templateId: string | undefined = undefined): Promise<void>
  {
    this.currentDesignStep.templateGroupId = id;

    for (const templateGroup of this.getTemplateGroups()) {
      templateGroup.isDefault = false;
      if (templateGroup.id === id) {
        templateGroup.isDefault = true;
      }
    }

    let templates = this.getTemplates();

    if (! templateId) {
      templateId = Object.keys(templates)[0];
      for (let _templateId of Object.keys(templates)) {
        let template = templates[_templateId];
        if (template.isDefault === true) {
          templateId = template.id;
          break;
        }
      }
    }
    await this.setTemplateId(templateId);
  }

  public getTemplateGroupId(): string | undefined
  {
    if (this.currentDesignStep !== undefined) {
      return this.currentDesignStep.templateGroupId;
    }
    else {
      const group = this.getTemplateGroups()[0];
      return group ? group.id : undefined;
    }
  }

  public getTemplateGroups(designState?: DesignState):
      Array<TemplateGroupConfig>
  {
    const state = designState || this.currentDesignStep;

    if (! state) {
      return [];
    }

    const templateGroups = this.getPart(state).templateGroups;

    return templateGroups.filter(group =>
        !group.conditions || group.conditions.every(condition =>
            this.conditionMatcher.isConditionFullfilled(condition)));
  }

  public getTemplateGroup(designState?: DesignState): TemplateGroupConfig
  {
    const state = designState || this.currentDesignStep;
    return Utils.find(
        this.getTemplateGroups(state), g => g.id === state.templateGroupId);
  }

  public getTemplatesSettings(): TemplatesSettings
  {
    return this.getDesignStepConfig().templatesSettings;
  }

  public async setTemplateId(templateId: string): Promise<void>
  {
    for (let _templateId of Object.keys(this.getTemplateGroup().templates)) {
      let template = this.getTemplateGroup().templates[_templateId];
      template.isDefault = false;
      if (template.id === templateId) {
        template.isDefault = true;
      }
    }
    this.currentDesignStep.templateId = templateId;
    await this.templateChanged.emit(this.getTemplate());
  }

  public removeTemplate(templateId: string): void
  {
    const templateGroup = this.getTemplateGroup();
    delete templateGroup.templates[templateId];
  }

  public getTemplateId(): string | undefined
  {
    return this.currentDesignStep
        ? this.currentDesignStep.templateId
        : undefined;
  }

  public getTemplates(designState?: DesignState):
      {[templateId: string]: TemplateConfig}
  {
    const state = designState || this.currentDesignStep;

    if (! state) {
      return {};
    }

    return this.getTemplateGroup(state).templates;
  }

  public getTemplate(designState?: DesignState): TemplateConfig
  {
    const state = designState || this.currentDesignStep;
    return this.getTemplates(state)[state.templateId];
  }

  public getTemplateById(templateGroupId: string,
                         templateId: string): TemplateConfig
  {
    return Utils
      .find(this.getTemplateGroups(), g => g.id === templateGroupId)
      .templates[templateId];
  }

  public setSVGStructure(designState: DesignState, svgStructure: string): void
  {
    this.getTemplate(designState).stageConfig.svgStructure = svgStructure;
  }

  public getSVGStructureUrl(): string
  {
    return this.getTemplate().stageConfig.svgStructureURL;
  }

  public getInitialSVGStructureUrl(): string
  {
    return this.currentDesignStep.initialSVGStructureUrl;
  }

  public setInitialSVGStructureUrl(url: string): void
  {
    this.currentDesignStep.initialSVGStructureUrl = url;
  }

  public getCurrentDesignState(): DesignState
  {
    return this.currentDesignStep;
  }

  public getDesignState(stepId: number, partId: string): DesignState
  {
    for (let designState of this.designSteps) {
      if (designState.designStepId === stepId &&
          designState.partId === partId) {
        return designState;
      }
    }
    return undefined;
  }

  public getPartLocations(): Array<PartLocation>
  {
    return Utils.flattenArray(this
        .productService
        .getCurrentProduct()
        .collageDesignerConfig
        .steps
        .map((step: StepConfig, idx: number) =>
            step.parts
                .map((part: PartConfig) => <PartLocation> {
                    designStepId: idx,
                    part: part
                })));
  }

  public getPartLocationById(id: string): PartLocation
  {
    const parts = Utils.flattenArray(this
        .productService
        .getCurrentProduct()
        .collageDesignerConfig
        .steps
        .map((step: StepConfig, idx: number) =>
            step.parts
                .filter((part: PartConfig) => part.id === id)
                .map((part: PartConfig) => <PartLocation> {
                    designStepId: idx,
                    part: part
                })));

    return parts.shift();
  }

  public async getSVGStructureByPartId(id: string, designStepId?: number):
      Promise<string | undefined>
  {
    const candidate: PartLocation = (!designStepId)
      ? this.getPartLocationById(id)
      : this.getPartLocations()
        .filter(pl => pl.designStepId === designStepId)
        .shift();

    if (! candidate) {
      return undefined;
    }

    const state = this.getDesignState(
        candidate.designStepId, candidate.part.id);

    if (!state && candidate.part.structureUrl) {
      try {
        return await this.resolveTemplate(candidate.part.structureUrl);
      }
      catch (exception) {
        this.loggingService.log(
            'DesignStepService', Severity.WARNING, exception.toString());
        return undefined;
      }
    }

    if (!state) {
      return undefined;
    }

    const templateGroup = Utils.find(
        candidate.part.templateGroups, g => g.id === state.templateGroupId);

    if (!templateGroup) {
      return undefined;
    }

    const template = templateGroup.templates[state.templateId];

    if (!template) {
      return undefined;
    }

    return template.stageConfig.svgStructure;
  }

  private async resolveTemplate(svgStructureURL: string): Promise<string>
  {
    if (!this.resolvedTemplates[svgStructureURL]) {
      const resp = <any> await this.http.get(svgStructureURL,
          { observe: 'response', responseType: 'text' })
        .pipe(Utils.retryPipe())
        .toPromise()
        .catch((exc) => {
          throw exc;
        });
      this.resolvedTemplates[svgStructureURL] =
          StageUtils.ensureHasPapNamespace(resp.body);
    }

    return this.resolvedTemplates[svgStructureURL];
  }

  private getTemplateSelection(): TemplateSelection
  {
    return {
      templateGroupId: this.getTemplateGroupId(),
      templateId: this.getTemplateId(),
    };
  }

  public createNewPlaceholder(type: PlaceholderType): void
  {
    this.createNewPlaceholderInitialized.emit(type);
  }

  public setSplashTriggered(): void
  {
    const layerConfig = this.getDesignablePart().editingView.overlays;
    layerConfig.images = layerConfig.images.filter(ec => ec.role !== 'splash');
    layerConfig.inlineSVGs = layerConfig.inlineSVGs.filter(
        ec => ec.role !== 'splash');
    this.partChanged.emit(this.getPart());
  }
}



// WEBPACK FOOTER //
// ./src/app/collage-designer/design-step/design-step.service.ts